/**
 * Armazón de una ficha.
 *
 * Cabecera con lo que identifica al registro, acciones a la derecha y
 * pestañas cuyo estado **vive en la URL** (`?t=equipos`). Eso último no es
 * un detalle: una ficha con pestañas de la que no se puede enlazar una
 * pestaña concreta obliga a decir «entra en el cliente y pincha en
 * contratos» en lugar de pegar un enlace.
 */

import { useSearchParams } from 'react-router-dom';
import type { ReactNode } from 'react';

export interface Tab {
  id: string;
  label: string;
  /** Contador que se enseña junto al nombre, si se conoce. */
  count?: number | null;
  render: () => ReactNode;
}

interface Props {
  title: string;
  subtitle?: ReactNode;
  /** Distintivos junto al título: estado, avisos. */
  badges?: ReactNode;
  actions?: ReactNode;
  tabs: readonly Tab[];
  loading?: boolean;
  error?: string | null;
}

export function DetailShell({
  title,
  subtitle,
  badges,
  actions,
  tabs,
  loading,
  error,
}: Props): ReactNode {
  const [params, setParams] = useSearchParams();
  const current = tabs.find((tab) => tab.id === params.get('t')) ?? tabs[0];

  function select(id: string): void {
    const next = new URLSearchParams(params);
    if (id === tabs[0]?.id) next.delete('t');
    else next.set('t', id);
    setParams(next, { replace: true });
  }

  return (
    <section className="screen">
      <header className="detail__head">
        <div className="detail__title">
          <h1 className="screen__title">{title}</h1>
          {badges}
        </div>
        {subtitle && <p className="detail__subtitle">{subtitle}</p>}
        {actions && <div className="detail__actions">{actions}</div>}
      </header>

      {error !== null && error !== undefined && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      <nav className="tabs" aria-label="Secciones de la ficha">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab${tab.id === current?.id ? ' tab--on' : ''}`}
            aria-current={tab.id === current?.id ? 'page' : undefined}
            onClick={() => select(tab.id)}
          >
            {tab.label}
            {tab.count !== undefined && tab.count !== null && (
              <span className="tab__count">{tab.count}</span>
            )}
          </button>
        ))}
      </nav>

      {loading ? <p className="table__loading">Cargando…</p> : current?.render()}
    </section>
  );
}

/** Pareja etiqueta-valor de una ficha. */
export function Datum({
  label,
  children,
  help,
}: {
  label: string;
  children: ReactNode;
  help?: ReactNode;
}): ReactNode {
  return (
    <div className="datum">
      <dt>{label}</dt>
      <dd>{children === null || children === undefined || children === '' ? '—' : children}</dd>
      {help && <p className="datum__help">{help}</p>}
    </div>
  );
}

export function DatumGroup({ title, children }: { title?: string; children: ReactNode }): ReactNode {
  return (
    <section className="form__section">
      {title && <h2 className="form__section-title">{title}</h2>}
      <dl className="datum__grid">{children}</dl>
    </section>
  );
}
